import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Tooltip,
  useTheme,
  Collapse,
} from '@mui/material';
import { ExternalLink, AlertTriangle } from 'lucide-mui';
import { FileType } from './types';

interface FileListProps {
  files: {
    data: FileType[];
    pagination?: {
      page: number;
      total: number;
    };
  };
  selectedFiles: string[];
  showSelectedOnly: boolean;
  onFileSelect: (fileId: string) => void;
  onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  libraryStatus: string;
  setShowSelectedOnly: (show: boolean) => void;
  setSelectedFiles: (files: string[]) => void;
}

const FileList: React.FC<FileListProps> = ({
  files,
  selectedFiles,
  showSelectedOnly,
  onFileSelect,
  onScroll,
  libraryStatus,
  setShowSelectedOnly,
  setSelectedFiles,
}) => {
  const theme = useTheme();

  const noRecordingsMessage = (
    <Typography variant="body2" color="text.secondary">
      Please select a folder from the drawer first select recordings
    </Typography>
  );

  const formatDate = (date: string | Date) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const renderFileListContent = () => {
    const filteredFiles = showSelectedOnly
      ? files.data.filter((file) => selectedFiles.includes(file._id))
      : files.data;

    if (libraryStatus === 'pending') {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            px: 2,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (libraryStatus === 'failed') {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            px: 2,
          }}
        >
          <Typography variant="body2" color="error">
            Failed to load library. Please try again later.
          </Typography>
        </Box>
      );
    }

    if (filteredFiles.length === 0) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            px: 2,
          }}
        >
          {showSelectedOnly ? (
            <Typography variant="body2" color="text.secondary">
              No files selected
            </Typography>
          ) : (
            noRecordingsMessage
          )}
        </Box>
      );
    }

    return filteredFiles.map((file) => (
      <ListItem
        key={file._id}
        disablePadding
        secondaryAction={
          <Tooltip
            title={
              !file.textData?.transcriptUrl
                ? 'Transcript not available - cannot be selected'
                : ''
            }
            arrow
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                pr: '9px',
              }}
            >
              {file.textData?.transcriptUrl ? (
                <Checkbox
                  edge="end"
                  checked={selectedFiles.includes(file._id)}
                  onChange={() => onFileSelect(file._id)}
                  tabIndex={-1}
                  disableRipple
                />
              ) : (
                <AlertTriangle
                  sx={{
                    color: 'warning.main',
                    mr: -0.3,
                  }}
                />
              )}
            </Box>
          </Tooltip>
        }
        sx={{
          borderBottom: 1,
          borderColor: 'secondary.light',
          '&:last-child': {
            borderBottom: 0,
          },
          opacity: !file.textData?.transcriptUrl ? 0.7 : 1,
        }}
      >
        <ListItemButton
          onClick={() => file.textData?.transcriptUrl && onFileSelect(file._id)}
          disabled={!file.textData?.transcriptUrl}
          sx={{
            py: 1.5,
            px: 2,
            transition: theme.transitions.create('background-color'),
            '&:hover': {
              bgcolor: file.textData?.transcriptUrl
                ? 'background.default'
                : 'transparent',
            },
            height: 'auto',
            minHeight: '72px',
            cursor: file.textData?.transcriptUrl ? 'pointer' : 'not-allowed',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              mr: 2,
              '&:hover .hover-overlay': {
                opacity: 1,
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              const baseUrl = window.location.pathname.includes('/app')
                ? '/app/#'
                : '/#';
              window.location.href = `${baseUrl}/library/${file.teamId}/recents/${file._id}`;
            }}
          >
            <Avatar
              variant="rounded"
              src={file.thumbUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
                fontSize: '1rem',
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {file.name.charAt(0)}
            </Avatar>
            <Box
              className="hover-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                opacity: 0,
                transition: theme.transitions.create('opacity'),
                borderRadius: 1,
                cursor: 'pointer',
              }}
            >
              <ExternalLink
                sx={{
                  color: '#fff !important',
                  fontSize: '1.2rem',
                }}
              />
            </Box>
          </Box>
          <ListItemText
            primary={file.name}
            secondary={formatDate(file.createdAt)}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: selectedFiles.includes(file._id) ? 500 : 400,
              color: 'text.primary',
              sx: {
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.2,
                mb: 0.5,
                whiteSpace: 'normal',
                height: 'auto',
                pr: 4,
                fontSize: '0.875rem !important',
              },
            }}
            secondaryTypographyProps={{
              variant: 'caption',
              color: 'text.secondary',
              sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0,
                pr: 4,
                fontSize: '0.75rem',
              },
            }}
            sx={{
              my: 0,
              overflow: 'hidden',
              width: '100%',
              pr: 1,
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    ));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
      }}
    >
      <Box
        component="div"
        onScroll={onScroll}
        sx={{
          overflowY: 'auto',
          height: '100%',
        }}
      >
        <List disablePadding>{renderFileListContent()}</List>
      </Box>
      <Collapse in={selectedFiles.length > 0}>
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            px: 2,
            py: 1,
            display: 'flex',
            gap: 1,
            borderTop: 1,
            borderColor: 'secondary.light',
            bgcolor: 'background.paper',
          }}
        >
          <Chip
            label="Clear All"
            size="small"
            onClick={() => setSelectedFiles([])}
            sx={{
              fontSize: '0.75rem',
              height: '24px',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          />
          <Chip
            label={showSelectedOnly ? 'Show All' : 'Show Selected'}
            size="small"
            onClick={() => setShowSelectedOnly(!showSelectedOnly)}
            sx={{
              fontSize: '0.75rem',
              height: '24px',
              bgcolor: showSelectedOnly ? 'action.selected' : 'transparent',
              '&:hover': {
                bgcolor: showSelectedOnly ? 'action.selected' : 'action.hover',
              },
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default FileList;
